// src/components/admin/NewUserModal.jsx
import React, { useState } from "react";
import { Modal, Button, Form, Row, Col, Alert, Spinner, InputGroup } from "react-bootstrap";
import { getToken } from "../../utilities/authHelper";

const DEFAULT_ROLE = "USER"; // change if you want ADMIN by default
const PASSWORD_ALPHABET =
  "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%^&*";

const generateRandomPassword = (length = 16) => {
  const nextLength = Math.max(12, length);
  const alphabetLength = PASSWORD_ALPHABET.length;
  const webCrypto =
    typeof window !== "undefined" ? window.crypto || window.msCrypto : null;

  if (webCrypto?.getRandomValues) {
    const randomValues = new Uint32Array(nextLength);
    webCrypto.getRandomValues(randomValues);

    return Array.from(randomValues, (value) =>
      PASSWORD_ALPHABET[value % alphabetLength],
    ).join("");
  }

  return Array.from({ length: nextLength }, () => {
    const nextIndex = Math.floor(Math.random() * alphabetLength);
    return PASSWORD_ALPHABET[nextIndex];
  }).join("");
};

const NewUserModal = ({ show, onHide, onSuccess }) => {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    role: DEFAULT_ROLE,
    invitation_code: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [copyStatus, setCopyStatus] = useState("");

  const apiUrl = process.env.REACT_APP_API_URL;
  const copyStatusTone = copyStatus === "Password copied." ? "#0F9D58" : "#0D6EFD";

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((s) => ({ ...s, [name]: value }));
  };

  const reset = () => {
    setForm({
      first_name: "",
      last_name: "",
      email: "",
      password: "",
      role: DEFAULT_ROLE,
      invitation_code: "",
    });
    setShowPassword(false);
    setErrorMsg("");
    setSuccessMsg("");
    setCopyStatus("");
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onHide?.();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setSuccessMsg("");

    // Basic validation
    if (!form.first_name || !form.last_name || !form.email || !form.password || !form.role) {
      setErrorMsg("Please fill all required fields.");
      return;
    }

    try {
      setSubmitting(true);

      const res = await fetch(`${apiUrl}/admin/create_user`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({
          email: form.email,
          password: form.password,
          first_name: form.first_name,
          last_name: form.last_name,
          role: form.role,
          invitation_code: form.invitation_code || undefined, // optional
        }),
      });

      const data = await res.json();

      if (data?.success) {
        setSuccessMsg("User created successfully.");
        // Give a brief moment for the user to see success, then close
        setTimeout(() => {
          onSuccess?.();   // let parent refresh list
          handleClose();
        }, 600);
      } else {
        setErrorMsg(data?.errorObject?.message || "Failed to create user.");
      }
    } catch (err) {
      setErrorMsg("Network or server error while creating user.");
      console.error("create_user error:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleGeneratePassword = () => {
    setForm((currentForm) => ({
      ...currentForm,
      password: generateRandomPassword(),
    }));
    setShowPassword(true);
    setCopyStatus("");
  };

  const handleCopyPassword = async () => {
    if (!form.password) {
      setCopyStatus("Generate or enter a password first.");
      return;
    }

    try {
      await navigator.clipboard.writeText(form.password);
      setCopyStatus("Password copied.");
    } catch (error) {
      setCopyStatus("Unable to copy password.");
      console.error("copy password error:", error);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton>
        <div>
          <Modal.Title className="fw-bold">Create New User</Modal.Title>
          <div className="text-muted small mt-1">
            Add the user details and set an initial password.
          </div>
        </div>
      </Modal.Header>

      <Modal.Body>
        {errorMsg ? <Alert variant="danger" className="mb-3">{errorMsg}</Alert> : null}
        {successMsg ? <Alert variant="success" className="mb-3">{successMsg}</Alert> : null}

        <Form onSubmit={handleSubmit}>
          <Row className="g-3">
            <Col md={6}>
              <Form.Label>First Name<span className="text-danger">*</span></Form.Label>
              <Form.Control
                name="first_name"
                value={form.first_name}
                onChange={handleChange}
                placeholder="Enter first name"
                required
                disabled={submitting}
              />
            </Col>
            <Col md={6}>
              <Form.Label>Last Name<span className="text-danger">*</span></Form.Label>
              <Form.Control
                name="last_name"
                value={form.last_name}
                onChange={handleChange}
                placeholder="Enter last name"
                required
                disabled={submitting}
              />
            </Col>

            <Col md={12}>
              <Form.Label>Email<span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder="name@example.com"
                required
                disabled={submitting}
              />
            </Col>

            <Col md={6}>
              <Form.Label>Role<span className="text-danger">*</span></Form.Label>
              <Form.Select
                name="role"
                value={form.role}
                onChange={handleChange}
                required
                disabled={submitting}
              >
                <option value="USER">USER</option>
                <option value="ADMIN">ADMIN</option>
              </Form.Select>
            </Col>

            <Col md={6}>
              <Form.Label>Invitation Code</Form.Label>
              <Form.Control
                name="invitation_code"
                value={form.invitation_code}
                onChange={handleChange}
                placeholder="Optional"
                disabled={submitting}
              />
            </Col>

            <Col md={12}>
              <Form.Label>Password<span className="text-danger">*</span></Form.Label>
              <InputGroup>
                <Form.Control
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={form.password}
                  onChange={handleChange}
                  placeholder="Set an initial password"
                  required
                  disabled={submitting}
                />
                <Button
                  type="button"
                  variant="outline-secondary"
                  onClick={handleGeneratePassword}
                  disabled={submitting}
                >
                  Generate
                </Button>
                <Button
                  type="button"
                  variant="outline-secondary"
                  onClick={() => setShowPassword((s) => !s)}
                  disabled={submitting}
                >
                  {showPassword ? "Hide" : "Show"}
                </Button>
                <Button
                  type="button"
                  variant="outline-secondary"
                  onClick={handleCopyPassword}
                  disabled={submitting || !form.password}
                >
                  Copy
                </Button>
              </InputGroup>

              <div className="text-muted small mt-2">
                Copy this password now. It won't be visible after this modal is
                closed.
              </div>
              {copyStatus ? (
                <div
                  className="mt-2"
                  style={{
                    color: copyStatusTone,
                    fontWeight: 700,
                    fontSize: "0.84rem",
                  }}
                >
                  {copyStatus}
                </div>
              ) : null}
            </Col>
          </Row>

          <div className="d-flex justify-content-end gap-2 mt-4">
            <Button variant="secondary" onClick={handleClose} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={submitting}>
              {submitting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Creating...
                </>
              ) : (
                "Create User"
              )}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default NewUserModal;
